/**
 * VendorImpactCard — iter86.2 (Product Ops → Health)
 *
 * The vendor impact radar: vendor → features (Feature Registry) → active
 * tournaments (by upstream binding) → leagues. Blast radius stops at
 * leagues by design. Default load is DB-only; "Live check" re-runs with
 * the API-Sports status call (costs 1 request).
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radar, Loader2, Activity } from "lucide-react";

const STATUS_PILL = {
    ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    warn: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    down: "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function VendorImpactCard() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const ran = useRef(false);

    const load = useCallback(async (live) => {
        setBusy(true);
        try {
            setData(await api.vendorImpact(live));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Vendor impact failed");
        }
        setBusy(false);
    }, []);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        load(false);
    }, [load]);

    const rows = data?.rows || [];
    return (
        <div className="brutal-card p-4 space-y-3" data-testid="vendor-impact-card">
            <div className="flex items-center gap-3 flex-wrap">
                <Radar className="w-5 h-5 text-cyber shrink-0" />
                <div className="flex-1 min-w-[200px]">
                    <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200">
                        Vendor impact radar
                    </h3>
                    <p className="text-[10px] text-zinc-500">
                        vendor → features → active tournaments → leagues.
                        Source: <code>feature_registry.md</code> + Vendor Registry.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => load(true)}
                    disabled={busy}
                    data-testid="vendor-impact-live-btn"
                    className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-60"
                    title="Re-run including the API-Sports live status call (costs 1 request)"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                    {busy ? "Checking…" : "Live check"}
                </button>
            </div>

            {!data && <div className="text-xs text-zinc-500 italic">Resolving impact…</div>}

            {data && rows.some((r) => r.blast_scope === "cross_cutting") && (
                <p className="text-[10px] text-zinc-500 leading-relaxed border-l-2 border-zinc-800 pl-2" data-testid="vendor-impact-cross-cutting-help">
                    Some vendors — auth, email, payments, AI — sit behind
                    <span className="text-zinc-300 font-bold"> every </span>
                    league instead of feeding a specific sport. Those rows
                    show <span className="text-zinc-300 font-bold">"gates every league"</span>
                    and list the full active-league set, which is why the
                    same names repeat across those rows. Sport-feed vendors
                    (API-Sports, Slash Golf) list only the leagues
                    that actually bind to them.
                </p>
            )}

            <div className="space-y-1.5" data-testid="vendor-impact-rows">
                {rows.map((r) => (
                    <div
                        key={r.vendor}
                        className="border border-zinc-800 rounded p-2.5 text-[11px] bg-black/30 space-y-1"
                        data-testid={`vendor-impact-row-${slug(r.vendor)}`}
                    >
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-white">{r.vendor}</span>
                            <span
                                className={`pill text-[9px] font-bold uppercase border ${STATUS_PILL[r.status] || STATUS_PILL.warn}`}
                                data-testid={`vendor-impact-status-${slug(r.vendor)}`}
                            >
                                {r.status}
                            </span>
                            {!r.monitored && (
                                <span className="pill text-[9px] uppercase border-zinc-700 text-zinc-500">unmonitored</span>
                            )}
                            {r.detail && <span className="text-[10px] text-zinc-500">{r.detail}</span>}
                            <span
                                className="ml-auto text-[10px] text-zinc-400 font-bold"
                                data-testid={`vendor-impact-blast-${slug(r.vendor)}`}
                            >
                                {r.affected_tournaments.length > 0
                                    ? (r.blast_scope === "cross_cutting"
                                        ? `gates every league · ${r.affected_leagues.length} active`
                                        : `${r.affected_tournaments.length} active tournament(s) · ${r.affected_leagues.length} league(s)`)
                                    : (r.blast_scope === "cross_cutting"
                                        ? "gates every league · none active right now"
                                        : "no active tournaments on this vendor")}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {r.features.map((f) => (
                                <span
                                    key={f.feature_id}
                                    className={`pill text-[9px] font-mono border ${f.status === "planned" ? "border-zinc-700 text-zinc-500" : "border-cyber/30 text-cyber"}`}
                                    title={f.label}
                                >
                                    {f.feature_id}
                                </span>
                            ))}
                            {r.features.length === 0 && (
                                <span className="text-[10px] text-zinc-600 italic">no feature tags — edit the vendor to add feature IDs</span>
                            )}
                        </div>
                        {r.blast_scope === "cross_cutting" && r.cross_cutting_reason && (
                            <p
                                className="text-[10px] text-zinc-500 leading-relaxed"
                                data-testid={`vendor-impact-reason-${slug(r.vendor)}`}
                            >
                                {r.cross_cutting_reason}
                            </p>
                        )}
                        {r.affected_leagues.length > 0 && (
                            <p className="text-[10px] text-zinc-500">
                                {r.blast_scope === "cross_cutting" ? "Every active league: " : "Leagues: "}
                                {r.affected_leagues.slice(0, 8).join(", ")}
                                {r.affected_leagues.length > 8 ? ` +${r.affected_leagues.length - 8} more` : ""}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
